import React from 'react';
import PropTypes from 'prop-types';
import {Stack, Subheading, TextStyle, Icon, Tooltip} from '@shopify/polaris';
import {prefilledData} from '../helpers/prefilledData';
import {clearQuestionValidations} from '../helpers/validation';
import {updateValue} from '../../../actions/inputs';
import {fetchQuestions} from '../../../actions/questions';
import {replaceDynamicQuestions} from '../../../actions/steps';
import {setError} from '../../../actions/progress';
import {submitStep} from '../helpers/step';
import {notifyException} from '../../../../../../utilities/analytics/bugsnag';

export const TYPES_NEEDING_OPTIONS = ['radio', 'checkbox', 'select'];

export default class Input extends React.Component {
  constructor(props) {
    super(props);

    this.renderHeader = this.renderHeader.bind(this);
    this.renderLabel = this.renderLabel.bind(this);
    this.renderTooltip = this.renderTooltip.bind(this);
    this.renderDescription = this.renderDescription.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.renderErrorMessage = this.renderErrorMessage.bind(this);
  }

  componentDidMount() {
    const {input = {}} = this.props;
    const {prefillKey} = input;

    if (prefillKey) {
      this.handleChange(prefilledData(prefillKey));
    }
  }

  render() {
    const {input} = this.props;

    if (!input) {
      notifyException({
        error: new Error(
          `No input entity found for handle '${this.props.inputId}'`
        ),
        errorName: 'Invalid Input Render',
        meta: {
          inputId: this.props.inputId,
          questionId: this.props.questionId,
          stepId: this.props.currentStepId,
        },
      });
      return null;
    }

    return (
      <div>
        {this.renderHeader()}
        {this._render()}
      </div>
    );
  }

  renderErrorMessage() {
    const {input} = this.props;
    const {error} = input;

    if (error) {
      return (
        <div className="sm-error-wrapper" aria-live="polite">
          <Icon source="alert" color="red" />
          <TextStyle variation="negative"> {error}</TextStyle>
        </div>
      );
    }

    return null;
  }

  renderHeader() {
    const labelContent = this.renderLabel();
    const descriptionContent = this.renderDescription();
    const header = (
      <header>
        {labelContent}
        {descriptionContent}
      </header>
    );

    return labelContent || descriptionContent ? header : null;
  }

  renderLabel() {
    const {input} = this.props;
    const {label} = input;

    return label ? (
      <Stack spacing="tight">
        <Subheading>{label}</Subheading>
        {this.renderTooltip()}
      </Stack>
    ) : null;
  }

  renderTooltip() {
    const {input} = this.props;
    const {tooltip} = input;

    return tooltip ? (
      <Tooltip content={tooltip}>
        <img
          src="/images/services_marketplace/info.svg"
          alt="Info icon"
          height="16"
          width="16"
        />
      </Tooltip>
    ) : null;
  }

  renderDescription() {
    const {input} = this.props;
    const {description} = input;

    return description ? (
      <p>
        <TextStyle>{description}</TextStyle>
      </p>
    ) : null;
  }

  handleChange(value) {
    if (typeof this.props.onBeforeChange === 'function') {
      this.props.onBeforeChange(this, value);
    }

    const {input, question, currentStepId} = this.props;
    const {dynamic, other_inputs: otherInputIds = []} = question;

    this.props.updateValue(input.handle, value, question.handle, currentStepId);
    clearQuestionValidations(question.handle);

    if (!otherInputIds.includes(input.handle)) {
      switch (dynamic) {
        case 'question': {
          this.fetchQuestions(value);
          break;
        }

        case 'step': {
          submitStep(currentStepId, input.handle, value);
          break;
        }
      }
    }
  }

  /**
   * Using the value provided we will fetch the dynamic questions
   * associated with that selection. We then need to replace any
   * questions which had been loaded via the inputs previous selection
   * (if there was any) with the new question handles.
   *
   * @param {string} value
   */
  fetchQuestions(value) {
    const {input, question, currentStepId} = this.props;

    this.props
      .fetchQuestions(input.handle, value)
      .then(questions => {
        const newQuestionHandles = questions.map(
          newQuestion => newQuestion.handle
        );
        this.props.replaceDynamicQuestions(
          currentStepId,
          question.handle,
          newQuestionHandles
        );

        return questions;
      })
      .catch(
        this.props.setError.bind({
          message:
            'Unable to fetch the next question based on the selected values. Please try again shortly.',
        })
      );
  }

  static mapStateToProps(state, ownProps) {
    const {
      entities: {inputs, questions},
      progress: {currentStepId},
    } = state;
    const input = inputs[ownProps.inputId];
    const question = questions[ownProps.questionId];

    return {
      currentStepId,
      input,
      question,
    };
  }

  static mapDispatchToProps(dispatch) {
    return {
      updateValue: (inputHandle, inputValue, questionHandle, stepHandle) => {
        return dispatch(
          updateValue(inputHandle, inputValue, questionHandle, stepHandle)
        );
      },

      fetchQuestions: (inputId, inputValue) => {
        return dispatch(fetchQuestions(inputId, inputValue));
      },

      replaceDynamicQuestions: (
        stepHandle,
        questionHandle,
        newQuestionHandles
      ) => {
        return dispatch(
          replaceDynamicQuestions(
            stepHandle,
            questionHandle,
            newQuestionHandles
          )
        );
      },

      setError: error => {
        return dispatch(setError(error));
      },
    };
  }

  _render() {
    throw new Error(
      'Anything which extends Input must implement a _render function',
      this
    );
  }
}

Input.propTypes = {
  currentStepId: PropTypes.string.isRequired,
  questionId: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
  question: PropTypes.object.isRequired,
  inputId: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
  input: PropTypes.object.isRequired,
  updateValue: PropTypes.func.isRequired,
  fetchQuestions: PropTypes.func.isRequired,
  replaceDynamicQuestions: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  onBeforeChange: PropTypes.func,
};
